<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="handleClose"></div>

      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl transform transition-all max-h-[90vh] flex flex-col">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑分账业务' : '申请开通分账' }}</h3>
              <p class="text-sm text-slate-500 mt-0.5">商户信息来自已完成的拉卡拉进件或人工补录结果</p>
            </div>
            <button
              @click="handleClose"
              class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-6">
            <el-form
              ref="formRef"
              :model="formData"
              :rules="rules"
              label-width="140px"
              class="space-y-6"
            >
              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7h18M3 12h18M3 17h18"/>
                  </svg>
                  拉卡拉商户标识
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div class="text-xs text-slate-500 mb-1">银联商户号</div>
                    <div class="text-sm text-slate-900 font-mono break-all">{{ merchantIdentifiers.merchantNo || '-' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">内部商户号</div>
                    <div class="text-sm text-slate-900 font-mono break-all">{{ merchantIdentifiers.merInnerNo || '-' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-slate-500 mb-1">终端号</div>
                    <div class="text-sm text-slate-900 font-mono break-all">{{ merchantIdentifiers.termNo || '-' }}</div>
                  </div>
                </div>
              </div>

              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  分账开通信息
                </h4>

                <el-form-item label="联系手机" prop="contactMobile">
                  <input
                    v-model.trim="formData.contactMobile"
                    type="text"
                    placeholder="请输入联系手机号"
                    class="form-input w-full"
                  >
                </el-form-item>

                <el-form-item label="最低分账比例" prop="splitLowestRatio">
                  <div class="relative">
                    <input
                      v-model.number="formData.splitLowestRatio"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="如70或70.50"
                      class="form-input w-full pr-8"
                    >
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                </el-form-item>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <el-form-item label="分账范围" prop="splitRange">
                    <select v-model="formData.splitRange" class="form-select-native w-full">
                      <option value="MARK">标记交易分账</option>
                      <option value="ALL">全部交易分账</option>
                    </select>
                  </el-form-item>

                  <el-form-item label="分账依据" prop="sepFundSource">
                    <select v-model="formData.sepFundSource" class="form-select-native w-full">
                      <option value="TR">交易分账</option>
                      <option value="BA">余额分账</option>
                    </select>
                  </el-form-item>

                  <el-form-item label="发起方式" prop="splitLaunchMode">
                    <select v-model="formData.splitLaunchMode" class="form-select-native w-full">
                      <option value="MANUAL">手动分账</option>
                      <option value="AUTO">自动规则分账</option>
                      <option value="POINTRULE">指定规则分账</option>
                    </select>
                  </el-form-item>

                  <el-form-item label="提款类型" prop="settleType">
                    <select v-model="formData.settleType" class="form-select-native w-full">
                      <option value="01">主动提款</option>
                      <option value="03">交易自动结算</option>
                    </select>
                  </el-form-item>
                </div>

                <el-form-item
                  v-if="requiresSplitRuleSource"
                  label="规则来源"
                  prop="splitRuleSource"
                >
                  <select v-model="formData.splitRuleSource" class="form-select-native w-full">
                    <option value="">请选择规则来源</option>
                    <option value="MER">商户分账规则</option>
                    <option value="PLATFORM">平台分账规则</option>
                  </select>
                </el-form-item>

                <el-form-item label="退货承担模式" prop="returnAcctModel">
                  <select v-model="formData.returnAcctModel" class="form-select-native w-full">
                    <option value="">默认</option>
                    <option value="01">收款账户</option>
                    <option value="02">分账账户</option>
                    <option value="03">收款账户 + 分账账户</option>
                  </select>
                </el-form-item>
              </div>

              <div class="bg-amber-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-amber-800 mb-4 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z"/>
                  </svg>
                  委托授权材料
                </h4>

                <el-form-item label="电子合同编号" prop="eleContractNo">
                  <input
                    v-model.trim="formData.eleContractNo"
                    type="text"
                    placeholder="已电子签约时填写合同编号，可不上传委托书"
                    class="form-input w-full"
                  >
                </el-form-item>

                <el-form-item label="上传委托书" prop="splitEntrustFileName">
                  <div class="w-full">
                    <input
                      ref="fileInputRef"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      class="hidden"
                      @change="handleFileChange"
                    >
                    <div class="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        @click="triggerFileInput"
                        class="px-4 py-2 border border-dashed border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-2"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                        </svg>
                        {{ uploadingFile ? '上传中...' : (formData.splitEntrustFileName || '点击上传文件') }}
                      </button>
                      <span v-if="formData.splitEntrustFileName" class="text-sm text-slate-500">
                        已上传: {{ formData.splitEntrustFileName }}
                      </span>
                    </div>
                    <p class="mt-2 text-xs text-slate-500">未填写电子合同编号时，必须上传分账结算委托书；支持 PDF、JPG、PNG 格式。</p>
                  </div>
                </el-form-item>
              </div>
            </el-form>
          </div>

          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex-shrink-0">
            <button
              @click="handleClose"
              class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              取消
            </button>
            <button
              @click="handleSubmit"
              :disabled="submitting"
              class="btn-primary"
            >
              <span v-if="submitting">保存中...</span>
              <span v-else>保存</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { createFranchiseeAccount, updateFranchiseeAccount, uploadLakalaFile } from '@/api/commission'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  franchiseeId: {
    type: [Number, String],
    default: null
  },
  accountData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const formRef = ref(null)
const submitting = ref(false)
const uploadingFile = ref(false)
const fileInputRef = ref(null)

const getInitialFormData = () => ({
  franchiseeId: null,
  tenantId: null,
  contactMobile: '',
  splitLowestRatio: null,
  splitEntrustFileName: '',
  splitEntrustFilePath: '',
  splitRange: 'MARK',
  sepFundSource: 'TR',
  eleContractNo: '',
  splitLaunchMode: 'MANUAL',
  settleType: '01',
  splitRuleSource: '',
  returnAcctModel: '',
  attachList: []
})

const formData = reactive(getInitialFormData())

const isEdit = computed(() => !!props.accountData?.applyId)
const requiresSplitRuleSource = computed(() => ['AUTO', 'POINTRULE'].includes(formData.splitLaunchMode))
const merchantIdentifiers = computed(() => ({
  merchantNo: props.accountData?.merchantNo || '',
  merInnerNo: props.accountData?.merInnerNo || '',
  termNo: props.accountData?.termNo || ''
}))

const validateEntrustMaterial = (_rule, _value, callback) => {
  if (formData.eleContractNo || (formData.splitEntrustFileName && formData.splitEntrustFilePath)) {
    callback()
    return
  }
  callback(new Error('请上传分账结算委托书或填写电子合同编号'))
}

const rules = computed(() => ({
  contactMobile: [
    { required: true, message: '请输入联系手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  splitLowestRatio: [
    { required: true, message: '请输入最低分账比例', trigger: 'blur' }
  ],
  splitRuleSource: [
    { required: requiresSplitRuleSource.value, message: '请选择分账规则来源', trigger: 'change' }
  ],
  splitEntrustFileName: [
    { validator: validateEntrustMaterial, trigger: 'change' }
  ]
}))

const resetForm = () => {
  const initial = getInitialFormData()
  Object.keys(initial).forEach(key => {
    formData[key] = props.accountData?.[key] ?? initial[key]
  })
  formData.franchiseeId = props.franchiseeId
  formData.tenantId = props.accountData?.tenantId || props.franchiseeId
  if (!Array.isArray(formData.attachList)) {
    formData.attachList = []
  }
}

const resolveTargetTenantId = () => formData.tenantId || props.accountData?.tenantId || props.franchiseeId || formData.franchiseeId

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const handleFileChange = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
  if (!allowedTypes.includes(file.type)) {
    ElMessage.error('仅支持 PDF、JPG、PNG 格式')
    event.target.value = ''
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    ElMessage.error('文件大小不能超过 10MB')
    event.target.value = ''
    return
  }

  const targetTenantId = resolveTargetTenantId()
  if (!targetTenantId) {
    ElMessage.error('请选择加盟商后再上传附件')
    event.target.value = ''
    return
  }

  uploadingFile.value = true
  try {
    const formDataUpload = new FormData()
    formDataUpload.append('tenantId', String(targetTenantId))
    formDataUpload.append('attType', 'SPLIT_ENTRUST_FILE')
    formDataUpload.append('file', file)
    formDataUpload.append('remark', '分账结算委托书')

    const res = await uploadLakalaFile(formDataUpload)

    formData.splitEntrustFileName = res.lakalaFileName || file.name
    formData.splitEntrustFilePath = res.lakalaFilePath || ''
    formData.attachList = [{
      attachType: 'SPLIT_ENTRUST_FILE',
      attachName: res.lakalaFileName || file.name,
      attachStorePath: res.lakalaFilePath || ''
    }]

    await formRef.value?.validateField('splitEntrustFileName')
    ElMessage.success('文件上传成功')
  } catch (error) {
    console.error('文件上传失败:', error)
    ElMessage.error(error.message || '文件上传失败')
  } finally {
    uploadingFile.value = false
    event.target.value = ''
  }
}

const handleClose = () => {
  resetForm()
  emit('update:visible', false)
}

const compactPayload = (payload) => {
  return Object.entries(payload).reduce((result, [key, value]) => {
    if (value === '' || value === null || value === undefined) {
      return result
    }
    if (Array.isArray(value) && value.length === 0) {
      return result
    }
    result[key] = value
    return result
  }, {})
}

const buildSubmitData = () => compactPayload({
  franchiseeId: props.franchiseeId,
  tenantId: resolveTargetTenantId(),
  contactMobile: formData.contactMobile,
  splitLowestRatio: formData.splitLowestRatio,
  splitEntrustFileName: formData.splitEntrustFileName,
  splitEntrustFilePath: formData.splitEntrustFilePath,
  splitRange: formData.splitRange,
  sepFundSource: formData.sepFundSource,
  eleContractNo: formData.eleContractNo,
  splitLaunchMode: formData.splitLaunchMode,
  settleType: formData.settleType,
  splitRuleSource: formData.splitRuleSource,
  returnAcctModel: formData.returnAcctModel,
  attachList: formData.attachList
})

const handleSubmit = async () => {
  try {
    await formRef.value.validate()
    submitting.value = true

    const submitData = buildSubmitData()
    if (isEdit.value) {
      await updateFranchiseeAccount(submitData)
      ElMessage.success('更新成功')
    } else {
      await createFranchiseeAccount(submitData)
      ElMessage.success('申请已提交')
    }

    emit('success')
    handleClose()
  } catch (error) {
    console.error('保存分账业务失败:', error)
    if (error !== false) {
      ElMessage.error(error.message || '保存失败')
    }
  } finally {
    submitting.value = false
  }
}

watch(() => props.visible, (val) => {
  if (val) {
    resetForm()
  }
})
</script>
